import { Injectable } from '@angular/core';

export type TrainingProgressionValues = {
  first_lesson_date: string | null;
  hours_needed: number | null;
  hours_needed_updated_at: string | null;
  hours_needed_updated_by: string | null;
  total_hours_done: number;
  training_hours_done: number | null;
  remc_progress: number | null;
  last_practice_exam_score: number | null;
  exam_preparation_hours_done: number | null;
  exam_date: string | null;
  exam_score: number | null;
};

type StepAttribute = {
  title: string;
  cta_link: string;
  key: string;
};

type TrainingProgressionStepRaw = {
  title: string;
  description: string;
  status: 'done' | 'current';
  current_sub_step: number;
  max_sub_step: number;
  attributes: StepAttribute[];
  section_title: string;
};

type TrainingProgressionData = {
  values: TrainingProgressionValues;
  steps: TrainingProgressionStepRaw[];
};

export interface StepInfo {
  title: string;
  cta?: {
    label: string;
    link?: string;
  };
  chip?: {
    label: string;
    color: string;
  };
}

export interface ProcessedStep {
  title: string;
  description: string;
  cta?: {
    label: string;
    link?: string;
  };
  infos?: StepInfo[];
  doneInfos?: DoneInfos;
  progress: number;
  current: boolean;
  done: boolean;
  sectionTitle: string;
}

type DoneInfos = {
  label: string;
  chip: {
    label: string;
    color: string;
  };
};

export type ProcessedTrainingProgression = {
  steps: ProcessedStep[][];
  values: TrainingProgressionValues;
};

@Injectable({ providedIn: 'root' })
export class TrainingProgressionMapper {
  constructor() {}

  public mapTrainingProgressionData(data: TrainingProgressionData): ProcessedTrainingProgression {
    if (!data?.steps?.length) {
      return null;
    }

    return {
      steps: data.steps.map((step) => this.mapStep(step, data.values)),
      values: data.values,
    };
  }

  private mapStep(step: TrainingProgressionStepRaw, values: TrainingProgressionValues): ProcessedStep[] {
    const baseStep: ProcessedStep = {
      title: step.title,
      description: step.description,
      progress: this.calculateProgress(step),
      current: step.status === 'current',
      done: step.status === 'done',
      sectionTitle: step.section_title,
    };

    if (step.attributes?.length > 0) {
      const infos = this.mapStepInfos(step.attributes, values);
      if (infos.length > 0) {
        baseStep.infos = infos;
      }

      const emptyKey = step.attributes.find((attr) => attr.key === '');
      if (!!emptyKey) {
        baseStep.cta = {
          label: emptyKey.title,
          link: emptyKey.cta_link,
        };
      }
    }

    if (step.status === 'done') {
      baseStep.doneInfos = this.generateDoneInfos(step, values);
    }

    return [baseStep];
  }

  private mapStepInfos(attributes: StepAttribute[], values: TrainingProgressionValues): StepInfo[] {
    return attributes
      .filter((attr) => attr.key)
      .map((attr) => this.createStepInfo(attr, values))
      .filter((info) => info !== null) as StepInfo[];
  }

  private createStepInfo(attribute: StepAttribute, values: TrainingProgressionValues): StepInfo | null {
    const value = this.getValueByKey(attribute.key, values);

    const chipLabel = this.formatChipLabel(attribute.key, value);
    const chipColor = this.getChipColor(attribute.key, value);

    return {
      title: attribute.title,
      cta: {
        label: 'Voir le détail',
        link: attribute.cta_link,
      },
      chip: {
        label: chipLabel,
        color: chipColor,
      },
    };
  }

  private getValueByKey(key: string, values: TrainingProgressionValues): any {
    const valueMap: Record<string, any> = {
      first_lesson_date: values.first_lesson_date,
      hours_needed: values.hours_needed,
      total_hours_done: values.total_hours_done,
      training_hours_done: values.training_hours_done,
      remc_progress: values.remc_progress,
      last_practice_exam_score: values.last_practice_exam_score,
      exam_preparation_hours_done: values.exam_preparation_hours_done,
      exam_date: values.exam_date,
      exam_score: values.exam_score,
    };

    return valueMap[key] ?? null;
  }

  private formatChipLabel(key: string, value: any): string {
    if (value === null) {
      return this.getDefaultLabel(key);
    }

    switch (key) {
      case 'first_lesson_date':
      case 'exam_date':
        return this.formatDate(value);
      case 'hours_needed':
      case 'total_hours_done':
      case 'exam_preparation_hours_done':
      case 'training_hours_done':
        return `${value}h`;
      case 'remc_progress':
        return `${value}%`;
      case 'last_practice_exam_score':
        return value ? `${value} / 31` : 'à faire';
      case 'exam_score':
        return `${value} / 31`;
      default:
        return String(value);
    }
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private getDefaultLabel(key: string): string {
    const defaults: Record<string, string> = {
      first_lesson_date: 'À planifier',
      exam_date: 'En attente',
      last_practice_exam_score: 'à faire',
      exam_score: 'En attente',
    };
    return defaults[key] || 'En attente';
  }

  private getChipColor(key: string, value: any): string {
    if (value === null) {
      return 'neutral-6';
    }

    switch (key) {
      case 'first_lesson_date':
      case 'exam_date':
        return value ? 'secondary-4' : 'neutral-6';
      case 'remc_progress':
      case 'total_hours_done':
      case 'training_hours_done':
      case 'exam_preparation_hours_done':
        return value > 0 ? 'secondary-4' : 'neutral-6';
      case 'exam_score':
        return value >= 20 ? 'accent-4' : 'primary-4';
      case 'last_practice_exam_score':
        return value ? (value >= 20 ? 'accent-4' : 'primary-4') : 'neutral-6';
      default:
        return 'neutral-6';
    }
  }

  private calculateProgress(step: TrainingProgressionStepRaw): number {
    if (step.status === 'done') return 100;
    if (step.max_sub_step === 0) return 0;

    return Math.round((step.current_sub_step / (step.max_sub_step + 1)) * 100);
  }

  private generateDoneInfos(step: TrainingProgressionStepRaw, values: TrainingProgressionValues): DoneInfos | null {
    const doneInfosMap: Array<{ title: string; value: number | null; label: string | null }> = [
      { title: 'Inscription conduite', value: null, label: null },
      { title: 'Première leçon', value: null, label: null },
      { title: 'Évaluation de départ', value: values.hours_needed || 0, label: `Nombre d'heures estimées` },
      { title: 'Durée de formation', value: values.hours_needed || 0, label: `Évaluation initiale mise à jour` },
      { title: 'Formation de conduite', value: values.training_hours_done || 0, label: `Nombre d'heures réalisées` },
      {
        title: "Préparation à l'examen",
        value: values.exam_preparation_hours_done || 0,
        label: `Nombre d'heures réalisées`,
      },
      { title: 'Examen pratique', value: values.exam_score || 0, label: `Résultat obtenu` },
    ];

    if (doneInfosMap.find((h) => h.title === step.title).value === null) {
      return null;
    }

    if (step.title === 'Examen pratique' && doneInfosMap.find((h) => h.title === step.title)?.value !== null) {
      return {
        label: 'Résultat obtenu',
        chip: {
          label: `${values.exam_score} / 31`,
          color: values.exam_score >= 20 ? 'accent-4' : 'primary-4',
        },
      };
    }

    return {
      label: doneInfosMap.find((h) => h.title === step.title)?.label || '',
      chip: {
        label: `${doneInfosMap.find((h) => h.title === step.title)?.value || 0}h`,
        color: 'neutral-3',
      },
    };
  }
}
